<template>
  <div class="space-y-6">
    <notifications position="top center" />
    <!-- Header -->
    <div class="bg-white shadow-sm border rounded-lg">
      <div class="px-6 py-4 flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-gray-900">Salesforce Email Report</h1>
          <p class="text-sm text-gray-600 mt-1">
            Checks the most recent campaign's creator email across Contacts, Leads, Accounts, and Opportunities.
          </p>
        </div>
        <div class="flex items-center space-x-3">
          <button @click="copyEmail" :disabled="!email" class="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50">
            Copy Email
          </button>
          <button @click="fetchData" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center">
            <svg class="w-4 h-4 mr-2" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            Refresh
          </button>
        </div>
      </div>
      <div class="px-6 pb-4 text-sm text-gray-700">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-3">
          <div>
            <span class="text-gray-500">Campaign:</span>
            <span class="font-medium text-gray-900">{{ campaign?.name || 'Unknown' }}</span>
            <span v-if="campaign?.id" class="text-gray-400">#{{ campaign.id }}</span>
          </div>
          <div v-if="campaign?.created_at" class="text-gray-500">Created: {{ campaign.created_at }}</div>
          <div class="truncate">
            <span class="text-gray-500">Email:</span>
            <code class="bg-gray-100 rounded px-1.5 py-0.5">{{ email || '—' }}</code>
          </div>
          <!-- Search + filters -->
          <form @submit.prevent="onSearch" class="w-full flex flex-wrap items-center gap-3 mt-2">
            <input v-model="emailInput" type="email" placeholder="Override email (optional)" class="flex-1 min-w-[260px] rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2" />
            <label class="inline-flex items-center text-sm text-gray-700">
              <input type="checkbox" v-model="leadsConvertedOnly" class="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              Converted leads only
            </label>
            <label class="inline-flex items-center text-sm text-gray-700">
              <input type="checkbox" v-model="includePersonAccounts" class="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              Include person accounts
            </label>
            <label class="inline-flex items-center text-sm text-gray-700">
              <input type="checkbox" v-model="bypassCache" class="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              Force refresh (bypass cache)
            </label>
            <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Search</button>
            <button type="button" @click="exportCsv" class="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Export CSV</button>
          </form>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="bg-white border rounded-lg shadow-sm p-6">
      <div class="animate-pulse space-y-3">
        <div class="h-4 bg-gray-200 rounded w-1/3"></div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div class="h-24 bg-gray-200 rounded"></div>
          <div class="h-24 bg-gray-200 rounded"></div>
          <div class="h-24 bg-gray-200 rounded"></div>
          <div class="h-24 bg-gray-200 rounded"></div>
        </div>
        <div class="h-64 bg-gray-200 rounded"></div>
      </div>
    </div>

    <template v-else>
      <!-- Summary cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button @click="toggle('contacts')" class="text-left bg-white rounded-lg shadow p-5 border hover:shadow-md transition">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Contacts</p>
              <p class="text-3xl font-semibold text-blue-600">{{ counts.contacts }}</p>
            </div>
            <div class="bg-blue-50 rounded-full p-3">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5V4H2v16h5m10 0v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2m12 0H7"/></svg>
            </div>
          </div>
          <div class="mt-2 text-xs text-gray-500">Click to {{ expanded.contacts ? 'hide' : 'show' }} details</div>
        </button>

        <button @click="toggle('leads')" class="text-left bg-white rounded-lg shadow p-5 border hover:shadow-md transition">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Leads</p>
              <p class="text-3xl font-semibold text-indigo-600">{{ counts.leads }}</p>
            </div>
            <div class="bg-indigo-50 rounded-full p-3">
              <svg class="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 01-8 0m8 0a4 4 0 00-8 0m8 0v8a4 4 0 01-8 0V7"/></svg>
            </div>
          </div>
          <div class="mt-2 text-xs text-gray-500">Click to {{ expanded.leads ? 'hide' : 'show' }} details</div>
        </button>

        <button @click="toggle('accounts')" class="text-left bg-white rounded-lg shadow p-5 border hover:shadow-md transition">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Accounts</p>
              <p class="text-3xl font-semibold text-emerald-600">{{ counts.accounts }}</p>
            </div>
            <div class="bg-emerald-50 rounded-full p-3">
              <svg class="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7l9-4 9 4-9 4-9-4zm0 6l9 4 9-4M3 7v6m18-6v6"/></svg>
            </div>
          </div>
          <div class="mt-2 text-xs text-gray-500">Click to {{ expanded.accounts ? 'hide' : 'show' }} details</div>
        </button>

        <button @click="toggle('opportunities')" class="text-left bg-white rounded-lg shadow p-5 border hover:shadow-md transition">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Opportunities</p>
              <p class="text-3xl font-semibold text-amber-600">{{ counts.opportunities }}</p>
            </div>
            <div class="bg-amber-50 rounded-full p-3">
              <svg class="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-2.21 0-4 .895-4 2s1.79 2 4 2 4 .895 4 2-1.79 2-4 2m0-8V6m0 12v2"/></svg>
            </div>
          </div>
          <div class="mt-2 text-xs text-gray-500">Click to {{ expanded.opportunities ? 'hide' : 'show' }} details</div>
        </button>
      </div>

      <!-- Details -->
      <div class="space-y-6">
        <!-- Contacts -->
        <div class="bg-white rounded-lg shadow border" v-if="expanded.contacts">
          <div class="px-6 py-4 border-b flex items-center justify-between">
            <h2 class="text-lg font-medium text-gray-900">Contacts</h2>
            <div class="flex items-center gap-3">
              <span class="text-sm text-gray-500">{{ counts.contacts }} found</span>
              <span v-if="counts.contacts > 1" class="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Duplicates detected</span>
              <span v-if="counts.leads > 0 && counts.contacts > 0" class="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">Lead also exists</span>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                  <th class="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="c in details.contacts" :key="c.Id" :class="counts.contacts > 1 ? 'bg-yellow-50' : ''">
                  <td class="px-6 py-3 text-sm text-gray-900">{{ (c.FirstName || '') + ' ' + (c.LastName || '') }}</td>
                  <td class="px-6 py-3 text-sm text-gray-700">{{ c.Email }}</td>
                  <td class="px-6 py-3 text-sm text-gray-700">{{ c.AccountId || '—' }}</td>
                  <td class="px-6 py-3 text-sm text-right">
                    <a :href="sfLink('Contact', c.Id)" target="_blank" class="text-blue-600 hover:text-blue-800 hover:underline">Open</a>
                  </td>
                </tr>
                <tr v-if="details.contacts.length === 0">
                  <td colspan="4" class="px-6 py-6 text-center text-sm text-gray-500">No contacts found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Leads -->
        <div class="bg-white rounded-lg shadow border" v-if="expanded.leads">
          <div class="px-6 py-4 border-b flex items-center justify-between">
            <h2 class="text-lg font-medium text-gray-900">Leads</h2>
            <div class="flex items-center gap-3">
              <span class="text-sm text-gray-500">{{ counts.leads }} found</span>
              <span v-if="counts.leads > 1" class="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Duplicates detected</span>
              <span v-if="counts.leads > 0 && counts.contacts > 0" class="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">Contact also exists</span>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Converted</th>
                  <th class="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="l in details.leads" :key="l.Id" :class="counts.leads > 1 ? 'bg-yellow-50' : ''">
                  <td class="px-6 py-3 text-sm text-gray-900">{{ (l.FirstName || '') + ' ' + (l.LastName || '') }}</td>
                  <td class="px-6 py-3 text-sm text-gray-700">{{ l.Email }}</td>
                  <td class="px-6 py-3 text-sm text-gray-700">{{ l.Company || '—' }}</td>
                  <td class="px-6 py-3 text-sm">
                    <span v-if="l.IsConverted" class="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Yes</span>
                    <span v-else class="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">No</span>
                  </td>
                  <td class="px-6 py-3 text-sm text-right">
                    <a :href="sfLink('Lead', l.Id)" target="_blank" class="text-blue-600 hover:text-blue-800 hover:underline">Open</a>
                  </td>
                </tr>
                <tr v-if="details.leads.length === 0">
                  <td colspan="5" class="px-6 py-6 text-center text-sm text-gray-500">No leads found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Accounts -->
        <div class="bg-white rounded-lg shadow border" v-if="expanded.accounts">
          <div class="px-6 py-4 border-b flex items-center justify-between">
            <h2 class="text-lg font-medium text-gray-900">Accounts</h2>
            <span class="text-sm text-gray-500">{{ counts.accounts }} found</span>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PersonEmail</th>
                  <th class="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="a in details.accounts" :key="a.Id">
                  <td class="px-6 py-3 text-sm text-gray-900">{{ a.Name }}</td>
                  <td class="px-6 py-3 text-sm text-gray-700">{{ a.PersonEmail || '—' }}</td>
                  <td class="px-6 py-3 text-sm text-right">
                    <a :href="sfLink('Account', a.Id)" target="_blank" class="text-blue-600 hover:text-blue-800 hover:underline">Open</a>
                  </td>
                </tr>
                <tr v-if="details.accounts.length === 0">
                  <td colspan="3" class="px-6 py-6 text-center text-sm text-gray-500">No accounts found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Opportunities -->
        <div class="bg-white rounded-lg shadow border" v-if="expanded.opportunities">
          <div class="px-6 py-4 border-b flex items-center justify-between">
            <h2 class="text-lg font-medium text-gray-900">Opportunities</h2>
            <span class="text-sm text-gray-500">{{ counts.opportunities }} found</span>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                  <th class="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="o in details.opportunities" :key="o.Id">
                  <td class="px-6 py-3 text-sm text-gray-900">{{ o.Name }}</td>
                  <td class="px-6 py-3 text-sm text-gray-700">{{ o.StageName || '—' }}</td>
                  <td class="px-6 py-3 text-sm text-gray-700">{{ formatCurrency(o.Amount) }}</td>
                  <td class="px-6 py-3 text-sm text-gray-700">{{ o.AccountId || '—' }}</td>
                  <td class="px-6 py-3 text-sm text-right">
                    <a :href="sfLink('Opportunity', o.Id)" target="_blank" class="text-blue-600 hover:text-blue-800 hover:underline">Open</a>
                  </td>
                </tr>
                <tr v-if="details.opportunities.length === 0">
                  <td colspan="5" class="px-6 py-6 text-center text-sm text-gray-500">No opportunities found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'SalesforceEmailReport',
  data() {
    return {
      loading: true,
      email: '',
      campaign: null,
      counts: { contacts: 0, leads: 0, accounts: 0, opportunities: 0 },
      details: { contacts: [], leads: [], accounts: [], opportunities: [] },
      instanceUrl: '',
      expanded: { contacts: true, leads: true, accounts: false, opportunities: true },
      // Filters
      emailInput: '',
      leadsConvertedOnly: false,
      includePersonAccounts: true,
      bypassCache: false,
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const params = new URLSearchParams()
        if (this.emailInput && this.emailInput.trim()) params.set('email', this.emailInput.trim())
        if (this.leadsConvertedOnly) params.set('leads_converted_only', 'true')
        if (!this.includePersonAccounts) params.set('include_person_accounts', 'false')
        if (this.bypassCache) params.set('bypass_cache', 'true')
        const url = '/admin/salesforce/email-report/data' + (params.toString() ? ('?' + params.toString()) : '')
        const res = await fetch(url)
        const json = await res.json()
        if (!json.success) throw new Error(json.message || 'Failed to load data')
        this.email = json.email
        this.campaign = json.campaign
        this.counts = json.counts
        this.details = json.details
        this.instanceUrl = json.instance_url
        // Update filters with server echo if present
        if (json.filters) {
          this.leadsConvertedOnly = !!json.filters.leads_converted_only
          this.includePersonAccounts = !!json.filters.include_person_accounts
        }
      } catch (e) {
        console.error('Failed to fetch email report data', e)
        alert('Failed to load Salesforce email report: ' + (e.message || e))
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      this.fetchData()
    },
    sfLink(objectType, id) {
      if (!this.instanceUrl || !id) return '#'
      // Lightning record URL pattern is more reliable
      return `${this.instanceUrl}/lightning/r/${objectType}/${id}/view`
    },
    toggle(key) {
      this.expanded[key] = !this.expanded[key]
    },
    async copyEmail() {
      try {
        if (!this.email) return
        await navigator.clipboard.writeText(this.email)
        this.$notify({ type: 'success', text: 'Email copied to clipboard' })
      } catch (e) {
        console.error('Copy failed', e)
      }
    },
    exportCsv() {
      const params = new URLSearchParams()
      if (this.emailInput && this.emailInput.trim()) params.set('email', this.emailInput.trim())
      if (this.leadsConvertedOnly) params.set('leads_converted_only', 'true')
      if (!this.includePersonAccounts) params.set('include_person_accounts', 'false')
      if (this.bypassCache) params.set('bypass_cache', 'true')
      const url = '/admin/salesforce/email-report/export' + (params.toString() ? ('?' + params.toString()) : '')
      window.open(url, '_blank')
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return '—'
      const num = Number(value)
      if (isNaN(num)) return String(value)
      return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
    }
  },
  mounted() {
    try {
      const sp = new URLSearchParams(window.location.search)
      const qEmail = sp.get('email')
      if (qEmail) {
        this.emailInput = qEmail
      }
    } catch (e) {}
    this.fetchData()
  }
}
</script>

<style scoped>
</style>
